import { useCallback } from 'react';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePromoteSlideUpFlow, usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { withTimeout } from '#src/app/pages/psf/share/shared/utils';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

import { useShouldShowSharingGoalFlow } from '../useShouldShowSharingGoalFlow';

/**
 * How long a trigger waits for treatment to register before continuing regardless. A slow or failed
 * experiment-service call must never leave the supporter stuck on the share page.
 */
const TREAT_TIMEOUT_MS = 1500;

type Arm = 'ineligible' | 'control' | 'variant';

type Surface = 'super_recruiter' | 'share_message';

/** Anything the experiment service has not declared is served the page as it was. */
const ARMS_BY_VARIATION: Readonly<Record<string, Arm>> = { control: 'control', variant_1: 'variant' };

type Result = {
	arm: Arm;
	/** The share screen the slide-up runs on: the Super Recruiter initial screen, or the second share screen. */
	surface: Surface;
	/** Resolves whether treatment actually registered; callers must not gate the sheet on it. */
	awaitTreatment: () => Promise<boolean>;
	claimNavigation: () => boolean;
	/**
	 * Whether a footer tap may open the sheet: always on the Super Recruiter screen, otherwise only
	 * until its one showing.
	 */
	canShowSheet: () => boolean;
	markSheetShown: () => void;
	/**
	 * Changes on every bfcache restore. Read it before awaiting treatment and compare after: a
	 * mismatch means the supporter left and came back, and that continuation must not act.
	 */
	restoreEpoch: () => number;
};

/**
 * Second Promote Ask R1 arm resolution and deferred treatment for the promote slide-up.
 *
 * The slide-up runs on one screen per flow: the initial screen when the Super Recruiter flow renders
 * there (en-US), otherwise the second share screen (Next-7). Which experiment-service experiment a
 * market is bucketed into comes from its feature config row.
 *
 * `ineligible` is everyone the experiment cannot bucket — desktop, the other share screen of the
 * flow, a petition that is not promotable (its promote page would redirect away), a `fallback`
 * resolution such as the feature config being off, or an unrecognized variation.
 * `control` and `variant` are both real assignments, and both are treated, so control is a
 * measurable population; only `variant` renders the sheet.
 *
 * The experiment is fetched with `treat: false`, so treatment is registered when the supporter
 * actually triggers the flow. `awaitTreatment` resolves once `$exposure` has been sent, or once the
 * timeout expires, whichever comes first — callers await it before navigating away, otherwise the
 * page unloads first and control's exposure is lost. It never rejects, and treatment happens once
 * per petition per page mount. A timeout or an unregistered treat is reported to Sentry and the
 * flow carries on unattributed rather than withholding the sheet.
 *
 * `claimNavigation` returns true for the first caller only, and gates the automatic share redirect:
 * it and a footer tap can be waiting on the same treatment, and the redirect must stand down once a
 * tap has taken the supporter somewhere. A direct tap records its claim but never defers to it —
 * the supporter pressed the control, so something has to happen.
 */
export function usePromoteSlideUpExperiment(): Result {
	const {
		data: {
			initial,
			petition,
			experiments: { revenueSharePromoteSlideUpExperiment: experiment },
		},
	} = usePsfSharePageData();
	const isMobile = useIsMobile();
	const utilityContext = useUtilityContext();
	const flow = usePromoteSlideUpFlow();
	const surface: Surface = useShouldShowSharingGoalFlow() ? 'super_recruiter' : 'share_message';

	const isOnSurfaceScreen = surface === 'super_recruiter' ? initial : !initial;
	const isEligible = isOnSurfaceScreen && isMobile && petition.promotable && !experiment.fallback;
	const arm = resolveArm(isEligible, experiment.variation);
	const key = `${experiment.experimentName}:${petition.id}`;

	// Forcing `treated` is safe: the exposure hook still no-ops on fallback/override, and this only
	// runs after treatExperiment has confirmed the assignment was registered.
	const trackExposure = useTrackExperimentExposure({ ...experiment, treated: true }, { resetKeys: [petition.id] });

	const awaitTreatment = useCallback(async (): Promise<boolean> => {
		if (experiment.override || experiment.fallback || !experiment.config.enabled) return false;

		let pending = flow.treatments.get(key);

		if (!pending) {
			pending = treatAndExposeOnce(utilityContext, experiment.experimentName, trackExposure, () =>
				reportOnce(flow, key, 'psf_share_promote_slide_up_exposure_failed', async () =>
					utilityContext.errorReporter.report({
						error: new Error('Promote slide-up exposure did not send'),
						context: { reason: 'psf_share_promote_slide_up_exposure_failed' },
					}),
				),
			);
			flow.treatments.set(key, pending);
		}

		const registered = await withTimeout(pending, TREAT_TIMEOUT_MS);

		if (registered === undefined) {
			// The round-trip is still running, and stays cached: a second trigger must join it rather
			// than start a treat of its own and fire a second `$exposure`.
			reportOnce(flow, key, 'psf_share_promote_slide_up_treat_timeout', async () =>
				utilityContext.errorReporter.report({
					error: new Error('Promote slide-up treatment timed out'),
					context: { reason: 'psf_share_promote_slide_up_treat_timeout' },
				}),
			);

			return false;
		}

		if (!registered) {
			// Nothing was registered, so drop the key and let the next trigger try again.
			flow.treatments.delete(key);
			reportOnce(flow, key, 'psf_share_promote_slide_up_treat_unregistered', async () =>
				utilityContext.errorReporter.report({
					error: new Error('Promote slide-up treatment did not register'),
					context: { reason: 'psf_share_promote_slide_up_treat_unregistered' },
				}),
			);
		}

		return registered;
	}, [experiment, flow, key, utilityContext, trackExposure]);

	return {
		arm,
		surface,
		awaitTreatment,
		claimNavigation: useCallback(() => {
			if (flow.claimedNavigations.has(key)) return false;
			flow.claimedNavigations.add(key);

			return true;
		}, [flow, key]),
		canShowSheet: useCallback(() => surface === 'super_recruiter' || !flow.shownSheets.has(key), [surface, flow, key]),
		markSheetShown: useCallback(() => {
			flow.shownSheets.add(key);
		}, [flow, key]),
		restoreEpoch: useCallback(() => flow.restores.count(), [flow]),
	};
}

function resolveArm(isEligible: boolean, variation: string): Arm {
	if (!isEligible) return 'ineligible';

	return ARMS_BY_VARIATION[variation] ?? 'ineligible';
}

/**
 * Reports at most once per petition per page mount for a given `reason`, so a degraded
 * experiment-service call reports a signal without flooding Sentry on every subsequent tap.
 */
function reportOnce(
	flow: ReturnType<typeof usePromoteSlideUpFlow>,
	key: string,
	reason: string,
	report: () => Promise<unknown>,
): void {
	const issueKey = `${key}:${reason}`;
	if (flow.reportedIssues.has(issueKey)) return;
	flow.reportedIssues.add(issueKey);
	void report();
}

/**
 * Runs the treat + `$exposure` round-trip, resolving whether the assignment actually registered.
 * Never rejects — a failed treat or exposure must not block the supporter's navigation. Caching is
 * the caller's business; this only does the round-trip. A registered treat whose `$exposure` did
 * not send (resolved `false` or threw) still resolves `true`: the assignment is real and the
 * exposure hook fires once per mount, so a retry could not resend it. It is surfaced through
 * `onExposureFailed` instead.
 */
async function treatAndExposeOnce(
	utilityContext: UtilityContext,
	experimentName: string,
	trackExposure: () => Promise<boolean>,
	onExposureFailed: () => void,
): Promise<boolean> {
	let registered = false;
	try {
		registered = await treatExperiment(utilityContext, experimentName);
	} catch {
		// an exception is no different from an unregistered treat as far as the caller is concerned
	}
	if (!registered) return false;

	try {
		if (!(await trackExposure())) onExposureFailed();
	} catch {
		onExposureFailed();
	}

	return true;
}
